import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { style } from './lightbox-modal.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import type { LightboxMediaItem } from 'components/discovery/pdp/lightbox/interfaces.js'
import { CSEModal } from '@cloudsuite/mosaic-elements'
import { breakpoints } from '@cloudsuite/mosaic-shared/styles/breakpoints.js'
import { createSwipeGesture } from 'components/discovery/pdp/lightbox/lightbox-swipe.js'
import 'components/discovery/pdp/lightbox/components/lightbox-thumbnails/lightbox-thumbnails.js'
import 'components/discovery/pdp/lightbox/components/lightbox-media/lightbox-media.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'

export const lightboxModalHiddenFields = ['title'] as const

initializeIconsByRole({
  'csc-lightbox-modal.close': { name: 'xmark', style: 'light' },
  'csc-lightbox-modal.previous': { name: 'arrow-left', style: 'light' },
  'csc-lightbox-modal.next': { name: 'arrow-right', style: 'light' },
})

@customElement('csc-lightbox-modal')
export class CSCLightboxModal extends HiddenFieldsMixin(LitElement, lightboxModalHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Array })
  images: LightboxMediaItem[] = []

  @property({ type: Number, attribute: 'active-index' })
  activeIndex = 0

  @property({ type: String, attribute: 'product-title' })
  productTitle = ''

  @query('cse-modal')
  private modal!: CSEModal

  @state()
  private smallScreen = false

  private mediaQuery: MediaQueryList | null = null

  @state()
  private internalIndex = 0

  @state()
  private isOpen = false

  @state()
  private previousIndex: number | null = null

  @state()
  private slideDirection: 'left' | 'right' | null = null

  private animationTimeout: ReturnType<typeof setTimeout> | null = null

  override connectedCallback() {
    super.connectedCallback()
    this.mediaQuery = window.matchMedia(`(max-width: ${breakpoints.small}px)`)
    this.smallScreen = this.mediaQuery.matches
    this.mediaQuery.addEventListener('change', this.onMediaChange)
  }

  private onMediaChange = (event: MediaQueryListEvent) => {
    this.smallScreen = event.matches
  }

  private get activeItem(): LightboxMediaItem | undefined {
    return this.images[this.internalIndex]
  }

  private get hasPrevious(): boolean {
    return this.internalIndex > 0
  }

  private get hasNext(): boolean {
    return this.internalIndex < this.images.length - 1
  }

  override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties)
    if (changedProperties.has('activeIndex')) {
      this.internalIndex = this.activeIndex
    }
  }

  async open() {
    this.internalIndex = this.activeIndex
    this.isOpen = true
    await this.updateComplete
    this.patchFullscreenHeight()
    await this.modal?.open()
    ;(this.shadowRoot?.querySelector('.close-button') as HTMLElement)?.focus()
    this.addKeyboardListener()
  }

  private patchFullscreenHeight() {
    const shadow = this.modal?.shadowRoot
    if (!shadow || shadow.querySelector('style[data-fullscreen-height-fix]')) return
    const styleElement = document.createElement('style')
    styleElement.setAttribute('data-fullscreen-height-fix', '')
    styleElement.textContent = '.fullscreen { max-height: 100% !important; }'
    shadow.appendChild(styleElement)
  }

  async close() {
    this.isOpen = false
    this.removeKeyboardListener()
    await this.modal?.close()
    this.dispatchEvent(new CustomEvent('lightbox-modal-close', {
      bubbles: true,
      composed: true
    }))
  }

  private keydownHandler = (event: KeyboardEvent) => {
    switch (event.key) {
      case 'Escape':
        this.close()
        break
      case 'ArrowLeft':
        this.onPreviousClick()
        break
      case 'ArrowRight':
        this.onNextClick()
        break
    }
  }

  private addKeyboardListener() {
    document.addEventListener('keydown', this.keydownHandler)
  }

  private removeKeyboardListener() {
    document.removeEventListener('keydown', this.keydownHandler)
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    this.removeKeyboardListener()
    this.mediaQuery?.removeEventListener('change', this.onMediaChange)
    if (this.animationTimeout) clearTimeout(this.animationTimeout)
  }

  private slideTo(newIndex: number) {
    if (newIndex === this.internalIndex || newIndex < 0 || newIndex >= this.images.length) return

    if (this.animationTimeout) clearTimeout(this.animationTimeout)

    this.previousIndex = this.internalIndex
    this.slideDirection = newIndex > this.internalIndex ? 'left' : 'right'
    this.internalIndex = newIndex

    this.animationTimeout = setTimeout(() => {
      this.previousIndex = null
      this.slideDirection = null
      this.animationTimeout = null
    }, 350)

    this.dispatchSlideChange()
  }

  private onPreviousClick() {
    if (this.hasPrevious) {
      this.slideTo(this.internalIndex - 1)
    }
  }

  private onNextClick() {
    if (this.hasNext) {
      this.slideTo(this.internalIndex + 1)
    }
  }

  private onThumbnailClick(event: CustomEvent<{ index: number }>) {
    this.slideTo(event.detail.index)
  }

  private readonly swipeGesture = createSwipeGesture({
    onSwipeLeft: () => this.onNextClick(),
    onSwipeRight: () => this.onPreviousClick(),
  })

  private dispatchSlideChange() {
    this.dispatchEvent(new CustomEvent('lightbox-modal-slide-change', {
      detail: { index: this.internalIndex },
      bubbles: true,
      composed: true
    }))
  }

  private renderSlideMedia(item: LightboxMediaItem, className: string, playVideo = false) {
    const isVideo = item.kind === 'video'

    return html`
      <csc-lightbox-media
        class="${className}"
        .item="${item}"
        ?with-zoom="${!isVideo}"
        ?autoplay-video="${isVideo && playVideo}"
      ></csc-lightbox-media>
    `
  }

  private renderNavControls() {
    if (this.images.length <= 1) return nothing

    return html`
      <div class="nav-controls">
        ${this.hasPrevious ? html`
          <cse-icon-button
            class="nav-previous"
            variant="secondary"
            @click="${this.onPreviousClick}"
            aria-label="${t('previous_label', { ns: 'csc-lightbox-modal', default: 'Previous image' })}"
          >
            <cse-icon icon-role="csc-lightbox-modal.previous" size="md"></cse-icon>
          </cse-icon-button>
        ` : html`<div></div>`}
        ${this.hasNext ? html`
          <cse-icon-button
            class="nav-next"
            variant="secondary"
            @click="${this.onNextClick}"
            aria-label="${t('next_label', { ns: 'csc-lightbox-modal', default: 'Next image' })}"
          >
            <cse-icon icon-role="csc-lightbox-modal.next" size="md"></cse-icon>
          </cse-icon-button>
        ` : html`<div></div>`}
      </div>
    `
  }

  private renderHeader() {
    return html`
      <div slot="header" class="modal-header">
        ${!this.isHidden('title') && this.productTitle
          ? html`<cse-text variant="heading-4" class="modal-title">${this.productTitle}</cse-text>`
          : html`<div></div>`
        }
        <cse-icon-button
          class="close-button"
          variant="secondary"
          @click="${this.close}"
          aria-label="${t('close_label', { ns: 'csc-lightbox-modal', default: 'Close lightbox' })}"
        >
          <cse-icon icon-role="csc-lightbox-modal.close" size="md"></cse-icon>
        </cse-icon-button>
      </div>
    `
  }

  private renderImageContainer() {
    const activeItem = this.activeItem
    const previousItem =
      this.previousIndex !== null ? this.images[this.previousIndex] : undefined
    const isVideo = activeItem?.kind === 'video'

    return html`
      <div
        class="${classMap({
          'image-container': true,
          'is-video': isVideo,
          'slide-left': this.slideDirection === 'left',
          'slide-right': this.slideDirection === 'right',
        })}"
        @touchstart="${this.swipeGesture.onTouchStart}"
        @touchmove="${this.swipeGesture.onTouchMove}"
        @touchend="${this.swipeGesture.onTouchEnd}"
      >
        ${previousItem ? this.renderSlideMedia(previousItem, 'image-exit') : nothing}
        ${activeItem && (this.isOpen || !isVideo) ? this.renderSlideMedia(
          activeItem,
          this.slideDirection ? 'image-enter' : '',
          this.isOpen && isVideo,
        ) : nothing}
      </div>
    `
  }

  private renderThumbnails() {
    if (this.images.length <= 1) return nothing

    return html`
      <csc-lightbox-thumbnails
        .images="${this.images}"
        active-index="${this.internalIndex}"
        orientation="horizontal"
        alignment="center"
        @thumbnail-click="${this.onThumbnailClick}"
      ></csc-lightbox-thumbnails>
    `
  }

  override render() {
    return this.smallScreen ? this.renderMobileView() : this.renderDesktopView()
  }

  private renderDesktopView() {
    return html`
      <cse-modal size="large">
        ${this.renderHeader()}

        <div class="lightbox-modal-content">
          <div class="image-navigation-wrapper">
            ${this.renderImageContainer()}
            ${this.renderNavControls()}
          </div>

          ${this.renderThumbnails()}
        </div>
        <span slot="footer"></span>
      </cse-modal>
    `
  }

  private renderMobileView() {
    return html`
      <cse-modal class="mobile" size="large" show-fullscreen no-backdrop>
        ${this.renderHeader()}

        <div class="lightbox-modal-content">
          <div class="image-navigation-wrapper">
            ${this.renderImageContainer()}
          </div>
        </div>

        <div slot="footer" class="mobile-footer">
          ${this.renderNavControls()}
          ${this.renderThumbnails()}
        </div>
      </cse-modal>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-lightbox-modal': CSCLightboxModal
  }
}
